import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Close, ExpandMore } from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { stopWheelNudge } from '../../utils/numberInput';
import {
  ACCENT,
  ACCENT_TINT,
  BAD,
  BrandLocation,
  Loading,
  LocationPicker,
  LocationProblem,
  LocationTabs,
  ProblemsPanel,
  ResultPanel,
  StepHeader,
  idOf,
  matchClass,
  messageOf,
  plural,
  problemsOf,
  teachesAt,
  useBrandLocations,
  useLocationData,
} from './shared';

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  IMPORT CLASSES FROM ANOTHER LOCATION
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *   1  From     a location and the classes to bring over
 *   2  To       one or more other locations
 *   3  Adjust   a tab per destination: every class pre-filled from the
 *               source, each value editable (price, trial, instructor, room…)
 *
 * The instructor carries over when they also teach at the destination;
 * otherwise the card asks for one. A class the destination already has
 * (linked copy or same name) starts unticked. Imported copies are linked to
 * the source, so creating sessions across locations finds them.
 */

type Draft = {
  include: boolean;
  className: string;
  description: string;
  duration: string;
  price: string;
  isTrialEnabled: boolean;
  trialPrice: string;
  payAtStudio: boolean;
  instructor: string;
  seatLayout: string;
};

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (message: string) => void;
  anchorStudioId?: string | null;
  /** Destination ticked when the dialog opens (e.g. the location being viewed). */
  preselectTarget?: string | null;
}

const ImportClassesDialog: React.FC<Props> = ({ open, onClose, onCreated, anchorStudioId, preselectTarget }) => {
  const { creatable, readable, loading: locLoading, error: locError } = useBrandLocations(anchorStudioId);
  const data = useLocationData();
  const { load } = data;

  const [step, setStep] = useState(0);
  const [source, setSource] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [targets, setTargets] = useState<string[]>([]);
  const [tab, setTab] = useState('');
  const [drafts, setDrafts] = useState<Record<string, Record<string, Draft>>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [problems, setProblems] = useState<LocationProblem[] | null>(null);
  const [result, setResult] = useState<{ created: any[]; failed: any[] } | null>(null);

  useEffect(() => {
    if (!open) return;
    setStep(0); setSource(''); setPicked([]); setTargets(preselectTarget ? [preselectTarget] : []); setTab(''); setDrafts({});
    setSaving(false); setError(null); setProblems(null); setResult(null);
  }, [open, preselectTarget]);

  useEffect(() => { if (source) load([source]); }, [source, load]);
  useEffect(() => { if (targets.length) load(targets); }, [targets, load]);

  const sourceClasses: any[] = (source && data.get(source)?.classes) || [];
  const targetLocs: BrandLocation[] = creatable.filter((l) => targets.includes(l._id) && l._id !== source);
  const srcName = readable.find((l) => l._id === source)?.name ?? '';

  // Build each destination's drafts once its data is in.
  const targetsReady = targetLocs.every((l) => data.get(l._id));
  useEffect(() => {
    if (step !== 2 || !targetsReady) return;
    setDrafts((prev) => {
      const next = { ...prev };
      targetLocs.forEach((l) => {
        const ld = data.get(l._id)!;
        const cur = { ...(next[l._id] ?? {}) };
        picked.forEach((cid) => {
          if (cur[cid]) return;
          const c = sourceClasses.find((x) => x._id === cid);
          if (!c) return;
          const insId = idOf(c.instructor);
          const ins = ld.instructors.find((i: any) => i._id === insId);
          const srcRoom = (data.get(source)?.rooms ?? []).find((r: any) => r._id === idOf(c.seatLayout));
          const roomName = String(c.seatLayout?.name ?? srcRoom?.name ?? '').toLowerCase();
          const room = roomName ? ld.rooms.find((r: any) => String(r.name).toLowerCase() === roomName) : null;
          cur[cid] = {
            include: !matchClass(c, ld.classes),
            className: c.className ?? '',
            description: c.description ?? '',
            duration: String(c.duration ?? ''),
            price: String(c.price ?? ''),
            isTrialEnabled: !!c.isTrialEnabled,
            trialPrice: String(c.trialPrice ?? ''),
            payAtStudio: !!c.payAtStudio,
            instructor: ins && teachesAt(ins, l._id) ? insId : '',
            seatLayout: room?._id ?? '',
          };
        });
        next[l._id] = cur;
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, targetsReady, targets.join(','), picked.join(',')]);

  const upd = (sid: string, cid: string, patch: Partial<Draft>) => {
    setDrafts((d) => ({ ...d, [sid]: { ...d[sid], [cid]: { ...d[sid][cid], ...patch } } }));
    setProblems(null);
  };

  const issuesOf = (dr?: Draft): string[] => {
    if (!dr || !dr.include) return [];
    const out: string[] = [];
    if (!dr.className.trim()) out.push('name');
    if (!dr.description.trim()) out.push('description');
    if (!(Number(dr.duration) > 0)) out.push('duration');
    if (dr.price === '' || Number(dr.price) < 0) out.push('price');
    if (dr.isTrialEnabled && (dr.trialPrice === '' || Number(dr.trialPrice) < 0)) out.push('trial price');
    if (!dr.instructor) out.push('instructor');
    return out;
  };
  const issueCount = Object.fromEntries(targetLocs.map((l) => [l._id, picked.reduce((n, cid) => n + (issuesOf(drafts[l._id]?.[cid]).length ? 1 : 0), 0)]));
  const includedCount = (sid: string) => picked.filter((cid) => drafts[sid]?.[cid]?.include).length;
  const totalIncluded = targetLocs.reduce((n, l) => n + includedCount(l._id), 0);
  const totalIssues = targetLocs.reduce((n, l) => n + issueCount[l._id], 0);

  const submit = async () => {
    setSaving(true); setError(null); setProblems(null);
    const items: any[] = [];
    targetLocs.forEach((l) => {
      picked.forEach((cid) => {
        const dr = drafts[l._id]?.[cid];
        const c = sourceClasses.find((x) => x._id === cid);
        if (!dr?.include || !c) return;
        items.push({
          studioId: l._id,
          sourceClassId: cid,
          class: {
            className: dr.className.trim(),
            image: c.image,
            description: dr.description.trim(),
            duration: parseInt(dr.duration, 10),
            instructor: dr.instructor,
            classType: idOf(c.classType),
            price: parseFloat(dr.price),
            payAtStudio: dr.payAtStudio,
            isTrialEnabled: dr.isTrialEnabled,
            trialPrice: dr.isTrialEnabled ? parseFloat(dr.trialPrice) || 0 : 0,
            ...(c.sessionType && data.get(l._id)?.enableSessionTypes ? { sessionType: c.sessionType } : {}),
            ...(dr.seatLayout ? { seatLayout: dr.seatLayout } : {}),
          },
        });
      });
    });
    try {
      const out: any = await apiService.createClassesAtLocations(items);
      setResult(out);
      if (!out.failed?.length) onCreated(`${plural(out.created.length, 'class', 'classes')} imported from ${srcName}`);
    } catch (e: any) {
      const p = problemsOf(e);
      if (p) { setProblems(p); setTab(p[0].studioId); }
      else setError(messageOf(e, 'The classes could not be imported.'));
    } finally {
      setSaving(false);
    }
  };

  const pickAll = picked.length === sourceClasses.length && sourceClasses.length > 0;
  const steps = ['From', 'To', 'Adjust & import'];

  const renderTarget = (sid: string) => {
    const ld = data.get(sid);
    if (!ld || !drafts[sid]) return <Loading label="Loading this location's instructors and rooms…" />;
    const serverIssues = (problems ?? []).filter((p) => p.studioId === sid);
    return (
      <Box display="flex" flexDirection="column" gap={1.25}>
        {serverIssues.length > 0 && <Alert severity="error">{serverIssues.map((p, i) => <div key={i}>{p.message}</div>)}</Alert>}
        {picked.map((cid) => {
          const c = sourceClasses.find((x) => x._id === cid);
          const dr = drafts[sid][cid];
          if (!c || !dr) return null;
          const existing = matchClass(c, ld.classes);
          const issues = issuesOf(dr);
          return (
            <Accordion key={cid} disableGutters defaultExpanded={dr.include && issues.length > 0}
              sx={{ border: '1px solid', borderColor: dr.include && issues.length ? BAD : dr.include ? ACCENT : '#eee', borderRadius: '12px !important', '&:before': { display: 'none' }, boxShadow: 'none', opacity: dr.include ? 1 : 0.7 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box display="flex" alignItems="center" gap={1.25} width="100%" minWidth={0}>
                  <Checkbox size="small" checked={dr.include} onClick={(e) => e.stopPropagation()} onChange={(e) => upd(sid, cid, { include: e.target.checked })}
                    sx={{ p: 0.5, '&.Mui-checked': { color: ACCENT } }} inputProps={{ 'aria-label': `Import ${c.className}` }} />
                  {c.image && <Box component="img" src={c.image} alt="" sx={{ width: 44, height: 32, objectFit: 'cover', borderRadius: 1 }} />}
                  <Box minWidth={0} flex={1}>
                    <Typography fontWeight={600} noWrap>{dr.className || c.className}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      ₹{dr.price || '—'}{dr.isTrialEnabled ? ` · trial ₹${dr.trialPrice || 0}` : ''} · {ld.instructors.find((i: any) => i._id === dr.instructor)?.name ?? 'no instructor yet'}
                    </Typography>
                  </Box>
                  {existing && <Chip size="small" label="Already here" sx={{ bgcolor: '#fff4e5', color: '#b26a00' }} />}
                  {dr.include && issues.length > 0 && <Chip size="small" label={`Needs ${issues.join(', ')}`} sx={{ bgcolor: '#fdecea', color: BAD }} />}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {existing && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    This location already has "{existing.className}". Importing adds a second one; leave it unticked to skip.
                  </Alert>
                )}
                <Box display="flex" flexDirection="column" gap={2}>
                  <TextField label="Class name *" size="small" fullWidth value={dr.className} onChange={(e) => upd(sid, cid, { className: e.target.value })} />
                  <Box display="flex" gap={2} flexDirection={{ xs: 'column', sm: 'row' }}>
                    <TextField label="Price (₹) *" type="number" size="small" fullWidth value={dr.price} onWheel={stopWheelNudge}
                      onChange={(e) => upd(sid, cid, { price: e.target.value })}
                      helperText={String(c.price) !== dr.price ? `${srcName}: ₹${c.price}` : ' '} />
                    <TextField label="Duration (mins) *" type="number" size="small" fullWidth value={dr.duration} onWheel={stopWheelNudge}
                      onChange={(e) => upd(sid, cid, { duration: e.target.value })} helperText=" " />
                  </Box>
                  <Box display="flex" gap={2} alignItems="flex-start" flexDirection={{ xs: 'column', sm: 'row' }}>
                    <FormControlLabel sx={{ flex: 1 }} control={<Switch checked={dr.isTrialEnabled} onChange={(e) => upd(sid, cid, { isTrialEnabled: e.target.checked })} />} label="Offer a trial price" />
                    {dr.isTrialEnabled && (
                      <TextField label="Trial price (₹) *" type="number" size="small" sx={{ flex: 1 }} value={dr.trialPrice} onWheel={stopWheelNudge}
                        onChange={(e) => upd(sid, cid, { trialPrice: e.target.value })} />
                    )}
                  </Box>
                  <FormControl fullWidth size="small" error={!dr.instructor}>
                    <InputLabel>Instructor *</InputLabel>
                    <Select value={ld.instructors.some((i: any) => i._id === dr.instructor) ? dr.instructor : ''} label="Instructor *"
                      onChange={(e) => upd(sid, cid, { instructor: e.target.value })}>
                      {ld.instructors.length === 0 && <MenuItem disabled value="">No instructors teach here yet</MenuItem>}
                      {ld.instructors.map((i: any) => <MenuItem key={i._id} value={i._id}>{i.name}</MenuItem>)}
                    </Select>
                    <FormHelperText>
                      {dr.instructor
                        ? idOf(c.instructor) === dr.instructor ? `Same as at ${srcName}` : `${srcName}: ${c.instructor?.name ?? '—'}`
                        : `${c.instructor?.name ?? 'The instructor'} does not teach here. Choose someone who does.`}
                    </FormHelperText>
                  </FormControl>
                  {ld.rooms.length > 0 && (
                    <FormControl fullWidth size="small">
                      <InputLabel>Room</InputLabel>
                      <Select value={ld.rooms.some((r: any) => r._id === dr.seatLayout) ? dr.seatLayout : ''} label="Room" onChange={(e) => upd(sid, cid, { seatLayout: e.target.value })}>
                        <MenuItem value=""><em>No room, book by headcount</em></MenuItem>
                        {ld.rooms.map((r: any) => <MenuItem key={r._id} value={r._id}>{r.name} · {r.bookableCount} spots</MenuItem>)}
                      </Select>
                    </FormControl>
                  )}
                  <TextField label="Description *" size="small" fullWidth multiline minRows={2} value={dr.description} onChange={(e) => upd(sid, cid, { description: e.target.value })} />
                  <FormControlLabel control={<Switch checked={dr.payAtStudio} onChange={(e) => upd(sid, cid, { payAtStudio: e.target.checked })} />} label="Pay at studio" />
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    );
  };

  const sourceOptions = readable;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ pr: 6 }}>
        <Typography variant="h6" fontWeight={700}>Import classes</Typography>
        <Typography variant="body2" color="text.secondary">Copy classes from one location to others, and adjust them before they're created</Typography>
        <IconButton onClick={onClose} disabled={saving} sx={{ position: 'absolute', right: 12, top: 12 }} aria-label="Close"><Close /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ minHeight: 440 }}>
        {result ? (
          <ResultPanel created={result.created.map((c: any) => ({ location: c.location, label: `"${c.className}" imported` }))} failed={result.failed} />
        ) : (
          <>
            <StepHeader steps={steps} active={step} />
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            {problems && <ProblemsPanel problems={problems} onGo={setTab} />}

            {step === 0 && (
              locLoading ? <Loading label="Loading your locations…" /> : locError ? <Alert severity="error">{locError}</Alert> : (
                <Box display="flex" flexDirection="column" gap={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Import from *</InputLabel>
                    <Select value={source} label="Import from *" onChange={(e) => { setSource(e.target.value); setPicked([]); }}>
                      {sourceOptions.map((l) => <MenuItem key={l._id} value={l._id}>{l.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                  {source && !data.get(source) && <Loading label={`Loading ${srcName}'s classes…`} />}
                  {source && data.get(source) && (
                    sourceClasses.length === 0 ? <Alert severity="info">{srcName} has no classes yet.</Alert> : (
                      <Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="body2" color="text.secondary">{plural(picked.length, 'class', 'classes')} selected</Typography>
                          <Button size="small" onClick={() => setPicked(pickAll ? [] : sourceClasses.map((c) => c._id))}>{pickAll ? 'Clear all' : 'Select all'}</Button>
                        </Box>
                        <Box display="grid" gap={1} sx={{ gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' } }}>
                          {sourceClasses.map((c) => {
                            const on = picked.includes(c._id);
                            return (
                              <Box key={c._id} onClick={() => setPicked(on ? picked.filter((x) => x !== c._id) : [...picked, c._id])}
                                role="checkbox" aria-checked={on} tabIndex={0}
                                onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setPicked(on ? picked.filter((x) => x !== c._id) : [...picked, c._id]); } }}
                                sx={{ display: 'flex', gap: 1.25, alignItems: 'center', p: 1.25, borderRadius: 2, cursor: 'pointer', border: '1.5px solid', borderColor: on ? ACCENT : '#eee', bgcolor: on ? ACCENT_TINT : 'transparent', '&:focus-visible': { outline: `2px solid ${ACCENT}` } }}>
                                <Checkbox size="small" checked={on} tabIndex={-1} sx={{ p: 0, '&.Mui-checked': { color: ACCENT } }} />
                                {c.image && <Box component="img" src={c.image} alt="" sx={{ width: 48, height: 36, objectFit: 'cover', borderRadius: 1 }} />}
                                <Box minWidth={0}>
                                  <Typography fontWeight={600} noWrap>{c.className}</Typography>
                                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                                    ₹{c.price}{c.isTrialEnabled ? ` · trial ₹${c.trialPrice ?? 0}` : ''} · {c.duration} min · {c.instructor?.name ?? '—'}
                                  </Typography>
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    )
                  )}
                </Box>
              )
            )}

            {step === 1 && (
              <LocationPicker locations={creatable} selected={targets} onChange={setTargets} disabledIds={[source]} disabledReason="This is where you're importing from"
                helper={<Typography variant="body2" color="text.secondary">Next you can change the price, trial price, instructor and anything else for each location before importing.</Typography>} />
            )}

            {step === 2 && (
              <>
                <LocationTabs value={tab || targetLocs[0]?._id || ''} onChange={setTab} locations={targetLocs} issues={issueCount} withAllTab={false} />
                {!targetsReady ? <Loading label="Loading instructors and rooms…" /> : renderTarget(tab || targetLocs[0]?._id)}
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {result ? (
          <Button variant="contained" onClick={onClose} sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d45180' } }}>Done</Button>
        ) : (
          <>
            <Box flex={1}>
              {step === 2 && (
                <Typography variant="body2" color={totalIssues ? 'error' : 'text.secondary'}>
                  {totalIssues ? `${plural(totalIssues, 'class', 'classes')} still need details` : `${plural(totalIncluded, 'class', 'classes')} will be imported`}
                </Typography>
              )}
            </Box>
            {step > 0 && <Button onClick={() => setStep(step - 1)} disabled={saving}>Back</Button>}
            {step === 0 && <Button variant="contained" disabled={!picked.length} onClick={() => { setTargets(targets.filter((t) => t !== source)); setStep(1); }} sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d45180' } }}>Next</Button>}
            {step === 1 && <Button variant="contained" disabled={!targetLocs.length} onClick={() => { setTab(targetLocs[0]._id); setStep(2); }} sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d45180' } }}>Next</Button>}
            {step === 2 && (
              <Button variant="contained" disabled={saving || totalIssues > 0 || !totalIncluded || !targetsReady} onClick={submit}
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined} sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d45180' } }}>
                {saving ? 'Importing…' : `Import ${plural(totalIncluded, 'class', 'classes')}`}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportClassesDialog;
